import React, { useState, useEffect } from 'react';
import { auth, db, storage } from '../firebase';
import { collection, onSnapshot, doc, getDoc, addDoc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import './Dates.css';

const Dates = () => {
    const [dates, setDates] = useState([]);
    const [coupleId, setCoupleId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [error, setError] = useState(null);

    const [newDate, setNewDate] = useState('');
    const [newDateTo, setNewDateTo] = useState('');
    const [newDescription, setNewDescription] = useState('');
    const [newLocation, setNewLocation] = useState('');
    const [newLocationTo, setNewLocationTo] = useState('');
    
    const [editingDateId, setEditingDateId] = useState(null);
    const [editingDescription, setEditingDescription] = useState('');
    const [editingLocation, setEditingLocation] = useState('');
    const [editingLocationTo, setEditingLocationTo] = useState('');
    const [showDateTo, setShowDateTo] = useState(false);
    const [showLocationTo, setShowLocationTo] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [sortBy, setSortBy] = useState('date-asc'); // 'date-desc', 'date-asc', 'location-asc'

    useEffect(() => {
        const unsubAuth = auth.onAuthStateChanged(async (currentUser) => {
            if (currentUser) {
                const userDocRef = doc(db, 'users', currentUser.uid);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    setUser({ ...userData, uid: currentUser.uid });
                    if (userData.partnerId) {
                        const ids = [currentUser.uid, userData.partnerId].sort();
                        setCoupleId(ids.join('_'));
                    } else {
                        setLoading(false);
                        setError("Please link with a partner to use the dates journal.");
                    }
                }
            } else {
                setUser(null);
                setCoupleId(null);
                setLoading(false);
            }
        });
        return () => unsubAuth();
    }, []);

    useEffect(() => {
        if (!coupleId) return;

        const datesCollectionRef = collection(db, 'coupleData', coupleId, 'dates');
        
        let q;
        if (sortBy === 'date-asc') {
            q = query(datesCollectionRef, orderBy('date', 'asc'));
        } else if (sortBy === 'location-asc') {
            q = query(datesCollectionRef, orderBy('location', 'asc'));
        } else { // Default to 'date-desc'
            q = query(datesCollectionRef, orderBy('date', 'desc'));
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const datesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                // Convert Firestore Timestamp to JS Date object for consistent handling
                date: doc.data().date.toDate() 
            }));
            setDates(datesList);
            setLoading(false);
        }, (err) => {
            setError("Failed to fetch dates.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, [coupleId, sortBy]);

    const handleAddDate = async (e) => {
        e.preventDefault();
        if (!newDate || !newDescription || !coupleId) return;

        const datesCollectionRef = collection(db, 'coupleData', coupleId, 'dates');
        await addDoc(datesCollectionRef, {
            date: new Date(newDate.replace(/-/g, '/')),
            dateTo: newDateTo ? new Date(newDateTo.replace(/-/g, '/')) : null,
            description: newDescription,
            location: newLocation,
            locationTo: newLocationTo,
            photos: {} // Initialize photos object
        });

        setNewDate('');
        setNewDateTo('');
        setNewDescription('');
        setNewLocation('');
        setNewLocationTo('');
        setShowDateTo(false);
        setShowLocationTo(false);
    };

    const handleUpdateDate = async (dateId) => {
        const dateDocRef = doc(db, 'coupleData', coupleId, 'dates', dateId);
        await updateDoc(dateDocRef, {
            description: editingDescription,
            location: editingLocation,
            locationTo: editingLocationTo
        });
        setEditingDateId(null);
        setEditingDescription('');
        setEditingLocation('');
        setEditingLocationTo('');
    };

    const handleDeleteDate = async (dateId) => {
        if (window.confirm("Are you sure you want to delete this date?")) {
            const dateDocRef = doc(db, 'coupleData', coupleId, 'dates', dateId);
            await deleteDoc(dateDocRef);
        }
    };

    const handlePhotoUpload = async (e, dateId) => {
        const file = e.target.files[0];
        if (!file || !coupleId || !user) return;

        setUploading(true);
        const photoRef = ref(storage, `coupleData/${coupleId}/dates/${dateId}/${user.uid}`);
        
        try {
            await uploadBytes(photoRef, file);
            const photoURL = await getDownloadURL(photoRef);
            
            const dateDocRef = doc(db, 'coupleData', coupleId, 'dates', dateId);
            await updateDoc(dateDocRef, {
                [`photos.${user.uid}`]: photoURL
            });
        } catch (error) {
            console.error("Error uploading photo: ", error);
            // Handle error display to user
        } finally {
            setUploading(false);
        }
    };
    
    const startEditing = (date) => {
        setEditingDateId(date.id);
        setEditingDescription(date.description);
        setEditingLocation(date.location || '');
        setEditingLocationTo(date.locationTo || '');
    };

    const formatDisplayDate = (date, dateTo) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        const from = date.toLocaleDateString('en-US', options);
        if (dateTo) {
            const to = dateTo.toDate().toLocaleDateString('en-US', options);
            return `${from} - ${to}`;
        }
        return from;
    }

    const formatDisplayLocation = (location, locationTo) => {
        if (location && locationTo) {
            return `${location} & ${locationTo}`;
        }
        return location;
    }

    if (loading) return <div className="journal-container">Loading...</div>;
    if (error) return <div className="journal-container"><p>{error}</p></div>;
    if (!user) return <div className="journal-container"><p>Please log in to see your dates.</p></div>;

    return (
        <div className="journal-container">
            <h1 className="journal-title">Our Dates</h1>

            <form onSubmit={handleAddDate} className="add-date-form">
                <div className="date-inputs">
                    <input 
                        type="date" 
                        value={newDate} 
                        onChange={(e) => setNewDate(e.target.value)} 
                        required 
                        className="date-from"
                    />
                    {showDateTo && (
                        <input 
                            type="date" 
                            value={newDateTo} 
                            onChange={(e) => setNewDateTo(e.target.value)} 
                            className="date-to"
                        />
                    )}
                    <button type="button" onClick={() => setShowDateTo(!showDateTo)} className="toggle-date-to">
                        {showDateTo ? '−' : '+'}
                    </button>
                </div>
                <div className="location-inputs">
                    <input 
                        type="text"
                        value={newLocation}
                        onChange={(e) => setNewLocation(e.target.value)}
                        placeholder="Location (e.g., Disney Springs)"
                        className="location-from"
                    />
                    {showLocationTo && (
                        <input
                            type="text"
                            value={newLocationTo}
                            onChange={(e) => setNewLocationTo(e.target.value)}
                            placeholder="Second Location (optional)"
                            className="location-to"
                        />
                    )}
                    <button type="button" onClick={() => setShowLocationTo(!showLocationTo)} className="toggle-location-to">
                        {showLocationTo ? '−' : '+'}
                    </button>
                </div>
                <textarea 
                    value={newDescription} 
                    onChange={(e) => setNewDescription(e.target.value)} 
                    placeholder="What did you do?" 
                    required 
                />
                <button type="submit">Add Date</button>
            </form>

            <div className="sort-container">
                <label htmlFor="sort-by">Sort by: </label>
                <select id="sort-by" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="date-asc">First</option>
                    <option value="date-desc">Recent</option>
                    <option value="location-asc">Location</option>
                </select>
            </div>

            <div className="journal-entries">
                {dates.map((entry) => (
                    <div key={entry.id} className="journal-entry">
                        <h2 className="journal-entry-title">{formatDisplayDate(entry.date, entry.dateTo)}</h2>
                        {entry.location && <h3 className="journal-entry-location">{formatDisplayLocation(entry.location, entry.locationTo)}</h3>}
                        
                        {editingDateId === entry.id ? (
                            <div className="edit-mode">
                                <div className="location-inputs">
                                    <input 
                                        type="text"
                                        value={editingLocation}
                                        onChange={(e) => setEditingLocation(e.target.value)}
                                        placeholder="Location"
                                        className="location-from"
                                    />
                                    <input
                                        type="text"
                                        value={editingLocationTo}
                                        onChange={(e) => setEditingLocationTo(e.target.value)}
                                        placeholder="Second Location (optional)"
                                        className="location-to"
                                    />
                                </div>
                                <textarea 
                                    value={editingDescription} 
                                    onChange={(e) => setEditingDescription(e.target.value)}
                                />
                                <button onClick={() => handleUpdateDate(entry.id)}>Save</button>
                                <button onClick={() => setEditingDateId(null)}>Cancel</button>
                            </div>
                        ) : (
                            <div>
                                <p className="journal-entry-text">{entry.description}</p>
                                <div className="photo-section">
                                    <div className="photo-upload">
                                        <label htmlFor={`photo-upload-${entry.id}-${user.uid}`}>
                                            {entry.photos && entry.photos[user.uid] ? (
                                                <img src={entry.photos[user.uid]} alt="Your upload" className="date-photo" />
                                            ) : (
                                                <div className="upload-placeholder">
                                                    <span>+</span>
                                                    <p>Your Photo</p>
                                                </div>
                                            )}
                                        </label>
                                        <input 
                                            id={`photo-upload-${entry.id}-${user.uid}`}
                                            type="file" 
                                            onChange={(e) => handlePhotoUpload(e, entry.id)} 
                                            style={{ display: 'none' }}
                                            disabled={uploading}
                                        />
                                    </div>
                                    {user.partnerId && (
                                        <div className="partner-photo">
                                            {entry.photos && entry.photos[user.partnerId] ? (
                                                <img src={entry.photos[user.partnerId]} alt="Partner's upload" className="date-photo" />
                                            ) : (
                                                <div className="upload-placeholder">
                                                    <span>?</span>
                                                    <p>Partner's Photo</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="entry-actions">
                                    <button onClick={() => startEditing(entry)}>Edit</button>
                                    <button onClick={() => handleDeleteDate(entry.id)} className="delete-btn">Delete</button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Dates;